"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Angka count-up saat elemen masuk viewport — sekali jalan.
 *
 * Ditulis ulang 2026-09-25: (1) dukungan desimal (rating 4.8★ — versi lama
 * `Math.round` membulatkannya jadi 5), (2) easing ease-out-cubic alih-alih
 * linear, (3) `prefers-reduced-motion` → langsung nilai akhir.
 */
export function Counter({
  target,
  suffix = "",
  duration = 1200,
}: {
  target: number;
  suffix?: string;
  /** Durasi animasi ms. */
  duration?: number;
}) {
  // Desimal hanya bila target memang pecahan (4.8 → "4.8"; 10000 → "10.000").
  const decimals = Number.isInteger(target) ? 0 : 1;
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const finish = () => setValue(target);

    if (reduce) {
      finish();
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || started.current) return;
        started.current = true;
        const t0 = performance.now();
        const step = (now: number) => {
          const p = Math.min((now - t0) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
          const v = target * eased;
          setValue(decimals ? Math.round(v * 10) / 10 : Math.round(v));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      },
      { threshold: 0.4 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration, decimals]);

  return (
    <span ref={ref}>
      {value.toLocaleString("id-ID", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}
